import type { RunV1 } from "@hyvo/schema";
import { useMemo } from "react";
import { formatTokens } from "../lib/format";
import { isSwap } from "../lib/replay";
import styles from "./Tape.module.css";

type Item =
  | { kind: "post"; key: string; handle: string; text: string }
  | { kind: "buy" | "sell"; key: string; handle: string; amount: string };

/** The run as a slow ticker: a few posts and trades from every round. */
function tapeItems(run: RunV1): Item[] {
  const agents = new Map(run.crowd.agents.map((a) => [a.agentId, a]));
  const items: Item[] = [];
  for (const round of run.rounds) {
    round.posts.slice(0, 2).forEach((post) => {
      const author = agents.get(post.agentId);
      if (author) items.push({ kind: "post", key: post.id, handle: author.handle, text: post.text });
    });
    round.actions
      .filter((action) => isSwap(action) && action.status === "ok")
      .slice(0, 2)
      .forEach((action) => {
        const buy = action.kind === "swap_buy";
        items.push({
          kind: buy ? "buy" : "sell",
          key: `${round.n}-${action.agentId}`,
          handle: agents.get(action.agentId)?.handle ?? action.agentId,
          amount: `${formatTokens(buy ? action.amountOut! : action.amountIn!)} ${run.world.token.symbol}`,
        });
      });
  }
  return items.slice(0, 32);
}

export function Tape({ run }: { run: RunV1 }) {
  const items = useMemo(() => tapeItems(run), [run]);
  if (items.length === 0) return null;
  const row = (hidden: boolean) => (
    <ul className={styles.row} aria-hidden={hidden || undefined}>
      {items.map((item) =>
        item.kind === "post" ? (
          <li key={item.key}>
            <b>{item.handle}</b>
            <span className={styles.label}>synthetic agent</span>
            <span className={styles.text}>{item.text}</span>
          </li>
        ) : (
          <li key={item.key}>
            <b>{item.handle}</b> {item.kind === "buy" ? "bought" : "sold"}{" "}
            <span className={`num ${item.kind === "buy" ? styles.buy : styles.sell}`}>{item.amount}</span>
          </li>
        ),
      )}
    </ul>
  );
  return (
    <section className={styles.tape} aria-label="Posts and trades from the featured run">
      <div className={styles.track} style={{ animationDuration: `${items.length * 7}s` }}>
        {row(false)}
        {row(true)}
      </div>
    </section>
  );
}
